// Client-side gate on the Barcode column, run when Validate is clicked (Step 2 of the
// "Import Cut Plan" actual-flow wizard, SAAS-24979/80). Purely local - this runs before the
// SAP verification call, so a bad scan never has to make a round trip to find out it's bad.
//
// Each rule below is independently switchable: flip either flag to false to drop that rule
// without touching validateBarcodes() itself.
export const BARCODE_VALIDATION_CONFIG = {
  // Every scannable row must have a barcode. Flip off to let SAP decide whether an empty
  // scan is acceptable for a given WO, same as the mock demonstrated it can.
  requireNonEmpty: true,

  // A barcode may not appear under two different work orders. Repeating one across several
  // rows of the SAME work order is legitimate and stays allowed - a confirmation covers the
  // work order, not the line. Flip off to let SAP's own bulk validation catch clashes itself.
  requireUnique: true,
};

/**
 * The work order half of the duplicate key.
 *
 * NOTE: this is the MAT work order as displayed in the grid, e.g. "20605642_SOMEKIT". SAP is
 * told the GKN number alone ("20605642") - the server strips the KitType suffix on the way out
 * via GknInferredWosDescriptor.extractGknWoNumber. The two forms disagree in exactly one case:
 * a single GKN work order split into several MAT work orders by kit type, carrying the same
 * barcode. That reads as "two work orders" here and as "one work order" to SAP, so it is
 * reported as a clash even though SAP would accept it. Deliberate for now - the operator can
 * only reason about what the screen shows them - but if GKN wants those accepted, this
 * function is the one place to change.
 */
const duplicateKeyWorkOrder = (nestWithRoll) => (nestWithRoll.woBusinessId || '').trim();

// A sub-row only needs a barcode when it's actually reachable by the scanner - mirrors the
// exact eligibility SelectNestsSecondStep already uses to build the Enter-to-next-field scan
// sequence (buildRowScanSequence), so a disabled nest or a ply-less kit type is never required
// to carry one.
const isBarcodeRequired = (nest, nestWithRoll) => !nest.isRowDisabled && !nestWithRoll.isKitTypeHasPlies;

const rowKey = (nest, index) => `${nest.nestBusinessId}__${index}`;

/**
 * @param nests - the nests about to be submitted (i.e. SelectNestsSecondStep's selectedNests).
 * @returns Set of "nestBusinessId__subRowIndex" keys identifying every sub-row that failed
 * validation. Empty set means nothing to block on.
 */
export function validateBarcodes(nests) {
  let invalidKeys = new Set();

  if (!BARCODE_VALIDATION_CONFIG.requireNonEmpty && !BARCODE_VALIDATION_CONFIG.requireUnique) {
    return invalidKeys;
  }

  // barcode -> { workOrders: Set of distinct WOs it was scanned under, keys: every row carrying it }
  let usageByBarcode = new Map();

  (nests || []).forEach((nest) => {
    (nest.nestWithRollFromFile || []).forEach((nestWithRoll, index) => {
      if (!isBarcodeRequired(nest, nestWithRoll)) {
        return;
      }

      let key = rowKey(nest, index);
      let barcode = (nest.sapBarcode?.[index] || '').trim();

      if (!barcode) {
        if (BARCODE_VALIDATION_CONFIG.requireNonEmpty) {
          invalidKeys.add(key);
        }
        return; // an empty barcode can't also count toward a duplicate
      }

      if (!BARCODE_VALIDATION_CONFIG.requireUnique) {
        return;
      }

      let usage = usageByBarcode.get(barcode);

      if (!usage) {
        usage = {workOrders: new Set(), keys: []};
        usageByBarcode.set(barcode, usage);
      }

      // A missing WO is kept as its own distinct key ('') rather than skipped. Without a work
      // order there is nothing for SAP to correlate the confirmation against, so pairing such
      // a row with a real WO's barcode is precisely the clash this rule exists to catch.
      usage.workOrders.add(duplicateKeyWorkOrder(nestWithRoll));
      usage.keys.push(key);
    });
  });

  // The nest is not part of the key: the same work order may carry the same barcode on any
  // number of rows, in any number of nests. Only a barcode straddling two work orders is wrong,
  // and then every row holding it is highlighted - the operator cannot tell which side of the
  // pair is the typo from one end alone.
  usageByBarcode.forEach((usage) => {
    if (usage.workOrders.size > 1) {
      usage.keys.forEach((key) => invalidKeys.add(key));
    }
  });

  return invalidKeys;
}
